import { useState, useEffect, useRef } from 'react';
import { fetchChartData } from '../utils/api';
import type { ChartBucket, CountryBucket, IndustryBucket, FilterOptions, FilterState, MapPoint } from '../types';

const emptyOptions: FilterOptions = {
  investmentTypes: [],
  investmentStatuses: [],
  industries: [],
  industryTree: [],
  companies: [],
  federalStates: [],
};

export function useChartData(
  filters: FilterState,
  industryDrill?: { level: 1 | 2; label: string },
  chartClusterIds?: number[],
  industriesNarrow?: string[],
) {
  const [chartData, setChartData] = useState<ChartBucket[]>([]);
  const [countryData, setCountryData] = useState<CountryBucket[]>([]);
  const [countryClusterIds, setCountryClusterIds] = useState<Record<string, number[]>>({});
  const [industryData, setIndustryData] = useState<IndustryBucket[]>([]);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(emptyOptions);
  const [totalCount, setTotalCount] = useState(0);
  const [mapPoints, setMapPoints] = useState<MapPoint[]>([]);
  const [maxVolume, setMaxVolume] = useState(0);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const abortRef = useRef<AbortController | undefined>(undefined);

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        setLoading(true);
        const result = await fetchChartData(filters, controller.signal, industryDrill, chartClusterIds, industriesNarrow);
        setChartData(result.chartData);
        setCountryData(result.countryData);
        setCountryClusterIds(result.countryClusterIds);
        setIndustryData(result.industryData);
        setFilterOptions(result.filterOptions);
        setTotalCount(result.totalCount);
        setMapPoints(result.points);
        setMaxVolume(result.maxVolume);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // Keep previous data on error
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 150);

    return () => {
      clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, [
    filters.dateFrom,
    filters.dateTo,
    filters.federalStates.join(','),
    filters.investmentTypes.join(','),
    filters.investmentStatuses.join(','),
    filters.industries.join(','),
    filters.companies.join(','),
    filters.search,
    filters.spotlight,
    industryDrill?.level,
    industryDrill?.label,
    chartClusterIds?.join(','),
    industriesNarrow?.join(','),
  ]);

  return { chartData, countryData, countryClusterIds, industryData, filterOptions, totalCount, mapPoints, maxVolume, loading };
}
